import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Choose the best src for an archive: prefer the embedded base64 data URL
// (new format, stored in MongoDB), fall back to the legacy disk url.
const archiveSrc = (item) => item.image_data || `${BACKEND_URL}${item.url || ''}`;

// deterministic pseudo-random rotation/offset per index so polaroids feel scattered but stable
const seed = (i, n) => {
  const x = Math.sin(i * 9301 + 49297) * 233280;
  return (x - Math.floor(x)) * n - n / 2;
};

function Polaroid({ item, index, onOpen }) {
  const rotate = seed(index, 24);
  const yOffset = seed(index + 100, 28);
  const xOffset = seed(index + 200, 18);
  const tapeRotate = seed(index + 300, 12);
  const tapeOffset = seed(index + 400, 30);

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`archive-polaroid-${item.id}`}
      initial={{ opacity: 0, y: 30, rotate: rotate * 1.4, scale: 0.92 }}
      animate={{ opacity: 1, y: yOffset, x: xOffset, rotate, scale: 1 }}
      whileHover={{ rotate: 0, y: yOffset - 8, scale: 1.06, zIndex: 30 }}
      transition={{ type: 'spring', stiffness: 130, damping: 16, delay: Math.min(index * 0.05, 0.7) }}
      className={`relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-400/60 rounded-[6px] ${item.caption ? 'p-3 pb-11' : 'p-3'}`}
      style={{
        width: 'min(78vw, 230px)',
        background: 'linear-gradient(155deg, #1b1430 0%, #120c22 55%, #0d0818 100%)',
        border: '1px solid rgba(167, 139, 250, 0.18)',
        boxShadow:
          '0 26px 60px -18px rgba(0,0,0,0.85), 0 6px 16px rgba(124,58,237,0.18), inset 0 0 0 1px rgba(255,255,255,0.03)',
      }}
    >
      <span
        aria-hidden
        className="absolute -top-3 w-16 h-5"
        style={{
          left: `calc(50% + ${tapeOffset}px)`,
          transform: `translateX(-50%) rotate(${tapeRotate}deg)`,
          background:
            'linear-gradient(135deg, rgba(167,139,250,0.32), rgba(217,70,239,0.22))',
          border: '1px solid rgba(167,139,250,0.25)',
          backdropFilter: 'blur(4px)',
          boxShadow:
            '0 4px 10px rgba(0,0,0,0.35), inset 0 0 14px rgba(255,255,255,0.05)',
        }}
      />

      <div
        className="w-full aspect-square overflow-hidden rounded-[3px]"
        style={{
          boxShadow:
            'inset 0 0 0 1px rgba(167,139,250,0.18), 0 0 24px rgba(124,58,237,0.08) inset',
        }}
      >
        <img
          src={archiveSrc(item)}
          alt={item.caption || 'archive'}
          loading="lazy"
          className="w-full h-full object-cover select-none"
          draggable={false}
        />
      </div>

      {item.caption && (
        <div className="absolute bottom-2 left-0 right-0 px-4 text-center">
          <p
            className="text-[15px] leading-snug truncate text-violet-100/85"
            style={{
              fontFamily: "'Caveat', 'Comic Sans MS', cursive",
              textShadow: '0 0 12px rgba(167,139,250,0.25)',
            }}
            title={item.caption}
          >
            {item.caption}
          </p>
        </div>
      )}
    </motion.button>
  );
}

function Lightbox({ item, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="archive-lightbox"
    >
      <button
        onClick={onClose}
        data-testid="archive-lightbox-close"
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition"
      >
        <X size={18} />
      </button>
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
        className={`relative max-w-[90vw] max-h-[88vh] flex flex-col items-center rounded-[8px] ${item.caption ? 'p-4 pb-14' : 'p-4'}`}
        style={{
          background: 'linear-gradient(155deg, #1b1430 0%, #120c22 55%, #0d0818 100%)',
          border: '1px solid rgba(167, 139, 250, 0.22)',
          boxShadow:
            '0 40px 80px -20px rgba(0,0,0,0.9), 0 0 60px rgba(124,58,237,0.18), inset 0 0 0 1px rgba(255,255,255,0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="rounded-[4px] overflow-hidden"
          style={{ boxShadow: 'inset 0 0 0 1px rgba(167,139,250,0.2)' }}
        >
          <img
            src={archiveSrc(item)}
            alt={item.caption || 'archive'}
            className="max-w-full max-h-[72vh] object-contain block"
          />
        </div>
        {item.caption && (
          <p
            className="absolute bottom-3 left-0 right-0 px-6 text-center text-violet-100/90 text-lg"
            style={{
              fontFamily: "'Caveat', 'Comic Sans MS', cursive",
              textShadow: '0 0 14px rgba(167,139,250,0.3)',
            }}
          >
            {item.caption}
          </p>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function Archives() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => window.dispatchEvent(new Event('playArchives')), 0);
    return () => {
      clearTimeout(t);
      window.dispatchEvent(new Event('pauseArchives'));
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/archives`);
        if (alive) setItems(data);
      } catch (e) {
        if (alive) setError("couldn't load the archives. try again later.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#080611] text-white relative overflow-x-hidden" data-testid="archives-page">
      <Navbar />

      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E\")",
      }} />
      <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[700px] h-[700px] pointer-events-none rounded-full"
        style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.12) 0%, transparent 65%)' }} />

      <div className="relative max-w-[1200px] mx-auto px-6 pt-32 pb-24">
        <header className="mb-14 text-center">
          <p className="text-[11px] text-violet-300/80 tracking-[0.35em] uppercase mb-3">memory drawer</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            archives
          </h1>
        </header>

        {loading && (
          <p data-testid="archives-loading" className="text-center text-xs text-gray-500 tracking-widest uppercase">loading…</p>
        )}

        {!loading && error && (
          <p data-testid="archives-error" className="text-center text-xs text-rose-400 tracking-widest uppercase">{error}</p>
        )}

        {!loading && !error && items.length === 0 && (
          <div data-testid="archives-empty" className="mx-auto max-w-md text-center rounded-2xl border border-white/[0.06] bg-white/[0.02] py-16 px-6">
            <p className="text-sm text-gray-400">no pictures yet.</p>
            <p className="text-[11px] text-gray-600 tracking-widest uppercase mt-2">come back later</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div
            data-testid="archives-grid"
            className="flex flex-wrap justify-center gap-x-8 gap-y-14"
          >
            {items.map((it, i) => (
              <Polaroid key={it.id} item={it} index={i} onOpen={setOpen} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && <Lightbox item={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </div>
  );
}
